import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDataStore } from '@/stores/dataStore';
import { api } from '@/lib/api';
import { humanizeApiError } from '@/lib/humanizeApiError';
import type { ChannelSettingsSpec } from '@/lib/appEvents';
import { Button } from '@/components/Button';
import { Modal, TextField, Label, Input, TextArea, Alert } from '@heroui/react';
import { HashIcon, VolumeIcon, PlusIcon, ChevronDownIcon } from '@/components/icons';
import { SelectMenu } from '@/components/SelectMenu';
import { ColorSwatchPicker } from '@/components/ColorSwatchPicker';
import { NumberInput } from '@/components/NumberInput';
import { Switch } from '@/components/ui/Switch';
import { Field } from '@/components/ui/Field';
import { VOICE_QUALITY_TIERS, DEFAULT_VOICE_BITRATE } from '@/lib/voiceQuality';
import { accentHexOr } from '@/lib/theme';

/**
 * Channel modal. Two modes:
 *   { kind: 'create', guildId, type, parentId? }
 *   { kind: 'edit',   channel }
 *
 * Common fields: name, parent category (dropdown). Type-specific fields are
 * rendered based on the channel's type:
 *   - TEXT: topic + slowmode preset picker
 *   - VOICE: bitrate slider + user-limit picker
 *   - CATEGORY: nothing extra
 *
 * Edit mode also exposes a "Удалить канал" action with a confirmation step.
 *
 * Visual: same modal language as ServerModal (dark backdrop, blur, no
 * borders, shadow-card card).
 */

export type ChannelModalSpec = ChannelSettingsSpec;

const SLOWMODE_PRESETS: { value: number; labelKey: string }[] = [
  { value: 0, labelKey: 'gmod.slowmodeOff' },
  { value: 5, labelKey: 'gmod.slowmode5s' },
  { value: 10, labelKey: 'gmod.slowmode10s' },
  { value: 30, labelKey: 'gmod.slowmode30s' },
  { value: 60, labelKey: 'gmod.slowmode1m' },
  { value: 300, labelKey: 'gmod.slowmode5m' },
  { value: 900, labelKey: 'gmod.slowmode15m' },
  { value: 3600, labelKey: 'gmod.slowmode1h' },
  { value: 21600, labelKey: 'gmod.slowmode6h' },
];

// Bitrate options for the channel creator come from the shared
// VOICE_QUALITY_TIERS so the tier names match what listeners see in the
// channel header. The creator sees the kbps; listeners only see the word.
// The full 384 kbps mediasoup ceiling is intentionally not exposed: 128 → 256
// is barely audible for voice chat and costs ×3 bandwidth per listener.

export function ChannelModal(props: { spec: ChannelModalSpec | null; onClose: () => void }) {
  const { t } = useTranslation();
  const open = props.spec !== null;
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) setBusy(false); }, [open]);
  const spec = props.spec;
  const editTarget = spec?.kind === 'edit' ? spec.channel : null;
  const type = editTarget
    ? editTarget.type
    : spec?.kind === 'create'
      ? spec.type
      : 'TEXT';
  const Icon = type === 'VOICE' ? VolumeIcon : type === 'CATEGORY' ? null : HashIcon;
  const title = editTarget
    ? t('gmod.channelSettingsTitle', { name: editTarget.name ?? '(unnamed)' })
    : type === 'CATEGORY'
      ? t('gmod.newCategory')
      : type === 'VOICE'
        ? t('gmod.newVoiceChannel')
        : t('gmod.newTextChannel');

  return (
    <Modal.Backdrop className="app-viewport-overlay" isOpen={open} isDismissable={!busy} isKeyboardDismissDisabled={busy} onOpenChange={(v) => { if (!v && !busy) props.onClose(); }}>
      <Modal.Container size="lg" scroll="inside" className="app-panel-container app-channel-container">
        <Modal.Dialog className="app-panel-dialog app-channel-dialog">
          {!busy && <Modal.CloseTrigger />}
          <Modal.Header>
            <Modal.Heading>
              <span className="flex min-w-0 items-center gap-2">
                {Icon && <Icon className="h-5 w-5 text-muted" />}
                <span className="break-words">{title}</span>
              </span>
            </Modal.Heading>
          </Modal.Header>
          <Modal.Body className="app-channel-body">
            {spec && <ChannelForm key={spec.kind === 'edit' ? spec.channel.id : `${spec.guildId}-${spec.type}-${spec.parentId ?? ''}`}
              spec={spec} busy={busy} onBusy={setBusy} onClose={props.onClose} />}
          </Modal.Body>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}

function ChannelForm(props: { spec: ChannelModalSpec; busy: boolean; onBusy: (value: boolean) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const editTarget = props.spec.kind === 'edit' ? props.spec.channel : null;
  // Narrow: guild channels can only be TEXT/VOICE/CATEGORY/ANNOUNCE (DM/THREAD
  // never appear inside a server). The Channel.type union includes those for
  // compatibility with the broader API but the modal never sees them.
  type GuildChannelType = 'TEXT' | 'VOICE' | 'CATEGORY' | 'ANNOUNCE';
  const type: GuildChannelType = editTarget
    ? (editTarget.type as GuildChannelType)
    : props.spec.kind === 'create'
      ? props.spec.type
      : 'TEXT';
  const guildId = editTarget?.guildId ?? (props.spec.kind === 'create' ? props.spec.guildId : '');
  const guild = useDataStore((s) => s.guilds[guildId]);
  const upsertChannel = useDataStore((s) => s.upsertChannel);
  const removeChannel = useDataStore((s) => s.removeChannel);
  const selectChannel = useDataStore((s) => s.selectChannel);

  const init = editTarget;
  const [name, setName] = useState(
    init?.name ?? '',
  );
  const [topic, setTopic] = useState(init?.topic ?? '');
  const [parentId, setParentId] = useState<string | null>(
    init?.parentId ?? (props.spec.kind === 'create' ? props.spec.parentId ?? null : null),
  );
  const [slowmodeS, setSlowmodeS] = useState(init?.slowmodeS ?? 0);
  const [bitrateKbps, setBitrateKbps] = useState<number>(
    init?.bitrateKbps ?? DEFAULT_VOICE_BITRATE,
  );
  const [userLimit, setUserLimit] = useState<number>(init?.userLimit ?? 0);
  const [commentsEnabled, setCommentsEnabled] = useState(init?.commentsEnabled !== false);
  const [tournament, setTournament] = useState(init?.tournament === true);
  // CATEGORY accent colour — null = default. When set, channel names inside the
  // category render in this colour in the sidebar.
  const [color, setColor] = useState<string | null>(init?.color ?? null);
  const { busy, onBusy: setBusy } = props;
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [showOptions, setShowOptions] = useState(false);

  const categories = guild?.channels.filter((c) => c.type === 'CATEGORY') ?? [];

  function normalizeName(raw: string): string {
    if (type === 'CATEGORY') return raw.trim().toUpperCase();
    return raw.trim().toLowerCase().replace(/\s+/g, '-');
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (editTarget) {
        const ch = await api.updateChannel(editTarget.id, {
          name: normalizeName(name),
          // topic is shared by TEXT (channel topic) and CATEGORY (description)
          ...(type === 'TEXT' || type === 'CATEGORY'
            ? { topic: topic.trim() || null }
            : {}),
          ...(type !== 'CATEGORY' ? { parentId } : {}),
          ...(type === 'TEXT' ? { slowmodeS, commentsEnabled } : {}),
          ...(type === 'VOICE' ? { bitrateKbps, userLimit: userLimit || null, tournament } : {}),
          ...(type === 'CATEGORY' ? { color } : {}),
        });
        upsertChannel(ch);
      } else {
        const ch = await api.createChannel(guildId, {
          type,
          name: normalizeName(name),
          ...(topic.trim() ? { topic: topic.trim() } : {}),
          ...(parentId ? { parentId } : {}),
          ...(type === 'TEXT' && slowmodeS > 0 ? { slowmodeS } : {}),
          ...(type === 'VOICE' ? { bitrateKbps } : {}),
          ...(type === 'VOICE' && userLimit > 0 ? { userLimit } : {}),
          ...(type === 'VOICE' && tournament ? { tournament } : {}),
          ...(type === 'CATEGORY' && color ? { color } : {}),
        });
        upsertChannel(ch);
        if (type !== 'CATEGORY') selectChannel(ch.id);
      }
      props.onClose();
    } catch (err) {
      const h = humanizeApiError(err, t('gmod.saveFailed'));
      setError(h.hint ? `${h.title}. ${h.hint}` : h.title);
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    if (!editTarget || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteChannel(editTarget.id);
      if (editTarget.guildId) removeChannel(editTarget.guildId, editTarget.id);
      props.onClose();
    } catch (err) {
      const h = humanizeApiError(err, t('gmod.deleteFailed'));
      setError(h.hint ? `${h.title}. ${h.hint}` : h.title);
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="app-channel-form flex min-h-0 flex-1 flex-col gap-4">
      <div className="app-channel-fields scrollbar-thin flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
      <TextField isRequired fullWidth value={name} onChange={setName}>
        <Label>{t('gmod.nameLabel')}</Label>
        <Input
          autoFocus
          variant="secondary"
          placeholder={type === 'CATEGORY' ? t('gmod.categoryNamePlaceholder') : t('gmod.channelNamePlaceholder')}
          maxLength={100}
        />
      </TextField>

      {type !== 'CATEGORY' && categories.length > 0 && (
        <Field label={t('gmod.categoryLabel')}>
          <SelectMenu
            ariaLabel={t('gmod.categoryLabel')}
            value={parentId ?? '__none__'}
            onChange={(v) => setParentId(v === '__none__' ? null : v)}
            options={[
              { value: '__none__', label: t('gmod.noCategory') },
              ...categories.map((c) => ({ value: c.id, label: c.name ?? '(unnamed)' })),
            ]}
          />
        </Field>
      )}

      {!editTarget && (
        <button
          type="button"
          aria-expanded={showOptions}
          onClick={() => setShowOptions((value) => !value)}
          className="app-channel-options-toggle flex min-h-11 w-full items-center justify-between rounded-xl px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-default/40 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          {t('gmod.extraOptions')}
          <ChevronDownIcon className={`h-4 w-4 transition-transform ${showOptions ? 'rotate-180' : ''}`} />
        </button>
      )}

      {(editTarget || showOptions) && (
        <div id="channel-extra-options" className="flex flex-col gap-5">

      {type === 'TEXT' && (
        <>
          <TextField fullWidth>
            <Label>{t('gmod.topicLabel')}</Label>
            <TextArea
              variant="secondary"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={t('gmod.topicPlaceholder')}
              maxLength={1024}
              rows={2}
              className="scrollbar-thin resize-none"
            />
          </TextField>
          <Field label={t('gmod.slowmodeLabel')} hint={t('gmod.slowmodeHint')}>
            <SelectMenu
              ariaLabel={t('gmod.slowmodeLabel')}
              value={String(slowmodeS)}
              onChange={(v) => setSlowmodeS(Number(v))}
              options={SLOWMODE_PRESETS.map((p) => ({ value: String(p.value), label: t(p.labelKey) }))}
            />
          </Field>
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1">
            <div className="text-sm text-ink">{t('gmod.commentsTitle')}</div>
            <Switch
              checked={commentsEnabled}
              onChange={setCommentsEnabled}
              label={t('gmod.commentsTitle')}
              className="shrink-0"
            />
            <div className="col-span-2 text-xs leading-snug text-ink-muted">{t('gmod.commentsChannelHint')}</div>
          </div>
        </>
      )}

      {type === 'CATEGORY' && (
        <TextField fullWidth>
          <Label>{t('gmod.descriptionLabel')}</Label>
          <TextArea
            variant="secondary"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder={t('gmod.categoryDescriptionPlaceholder')}
            maxLength={1024}
            rows={2}
            className="scrollbar-thin resize-none"
          />
        </TextField>
      )}

      {type === 'CATEGORY' && (
        <Field label={t('gmod.categoryColorLabel')} hint={t('gmod.categoryColorHint')}>
          <div className="flex items-center gap-3">
            <Switch
              checked={color !== null}
              onChange={(on) => setColor(on ? (color ?? accentHexOr()) : null)}
              label={t('gmod.categoryColorLabel')}
            />
            {color !== null && <ColorSwatchPicker value={color} onChange={setColor} />}
          </div>
        </Field>
      )}

      {type === 'VOICE' && (
        <>
          <Field label={t('gmod.audioQualityLabel')}>
            <SelectMenu
              ariaLabel={t('gmod.audioQualityLabel')}
              value={String(bitrateKbps)}
              onChange={(v) => setBitrateKbps(Number(v))}
              options={VOICE_QUALITY_TIERS.map((tier) => ({
                value: String(tier.value),
                label: `${t(tier.labelKey)} · ${tier.value} kbps`,
                sublabel: t(tier.subtitleKey),
              }))}
            />
          </Field>
          <Field
            label={t('gmod.userLimitLabel')}
            hint={
              userLimit > 0
                ? t('gmod.userLimitHint', { count: userLimit })
                : t('gmod.userLimitNoneHint')
            }
          >
            <NumberInput
              value={userLimit}
              onChange={setUserLimit}
              min={0}
              max={99}
              className="w-36"
            />
          </Field>
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1">
            <div className="text-sm text-ink">{t('gmod.tournamentTitle')}</div>
            <Switch
              checked={tournament}
              onChange={setTournament}
              label={t('gmod.tournamentTitle')}
              className="shrink-0"
            />
            <div className="col-span-2 text-xs leading-snug text-ink-muted">{t('gmod.tournamentHint')}</div>
          </div>
        </>
      )}

        </div>
      )}

      {error && (
        <Alert status="danger">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>{error}</Alert.Title>
          </Alert.Content>
        </Alert>
      )}

      {editTarget && (
        <div className="app-panel-actions mt-4 pt-4">
          {confirmingDelete ? (
            <div className="flex flex-col gap-2 text-sm text-danger">
              <span>
                {type === 'CATEGORY'
                  ? t('gmod.deleteCategoryConfirm')
                  : t('gmod.deleteChannelConfirm')}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="danger" size="sm" className="min-h-11" disabled={busy} onClick={doDelete}>
                  {type === 'CATEGORY' ? t('gmod.deleteCategoryYesAll') : t('gmod.deleteYes')}
                </Button>
                <Button variant="ghost" size="sm" className="min-h-11" disabled={busy} onClick={() => setConfirmingDelete(false)}>
                  {t('gmod.no')}
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="ghost" size="sm" className="min-h-11 text-danger hover:bg-danger/8" disabled={busy} onClick={() => setConfirmingDelete(true)}>
              {type === 'CATEGORY' ? t('gmod.deleteCategory') : t('gmod.deleteChannel')}
            </Button>
          )}
        </div>
      )}
      </div>
        <div className="app-panel-actions flex shrink-0 flex-wrap items-center justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" className="min-h-11" disabled={busy} onClick={props.onClose}>
            {t('gmod.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            className="min-h-11"
            type="submit"
            disabled={!name.trim()}
            loading={busy}
          >
            {editTarget ? t('gmod.save') : t('gmod.create')}
          </Button>
        </div>
    </form>
  );
}

/**
 * Tiny convenience for callers — render a "+ Создать" button or whatever.
 * Re-exported so neighbours don't need to import the icons module too.
 */
export { PlusIcon };
